import {
  I18NState,
  LanguageIdentifier,
  NamespaceIdentifier,
  Translation,
  TranslationChangeListenerCallback,
  TranslationFlat,
} from "..";

/**
 * Runs the translation resolver for a given namespace and language.
 *
 * @param namespace - The namespace identifier.
 * @param language - The language identifier.
 * @returns A promise that resolves to a record of translations.
 */
async function runTranslationResolver(
  namespace: NamespaceIdentifier,
  language: LanguageIdentifier
): Promise<Record<string, string>> {
  const resolver = I18NState.getInstance().translationResolver.find(
    (r) => r.language === language && r.namespace === namespace
  );

  if (!resolver) {
    console.warn(
      `@opendash/i18n: No translation resolver found for language='${language}' and namespace='${namespace}'`
    );

    return {};
  }

  try {
    const translationResolved = await resolver.resolver();

    return flattenTranslation(
      "default" in translationResolved &&
        "__esModule" in translationResolved &&
        translationResolved.__esModule === true
        ? (translationResolved.default as Translation)
        : (translationResolved as Translation)
    );
  } catch (error) {
    console.error(
      `@opendash/i18n: Error in translation resolver found for language='${language}' and namespace='${namespace}'`,
      error
    );

    return {};
  }
}

/**
 * Runs the translation resolver for a given namespace and language.
 *
 * @param namespace - The namespace identifier.
 * @param language - The language identifier.
 * @returns A promise that resolves to a record of translations.
 */
export async function resolveTranslation(
  namespace: NamespaceIdentifier,
  language?: LanguageIdentifier
): Promise<void> {
  const STATE: I18NState = I18NState.getInstance();

  if (!language) {
    language = STATE.language;
  }

  if (!(language in STATE.languages)) {
    return;
  }

  const parentLanguage = STATE.languages[language]?.parent;

  if (!namespace || STATE.translations[language][namespace]) {
    return void 0;
  }

  if (STATE.namespacesResolving[language + "~" + namespace]) {
    return new Promise((resolve) => {
      const cancel = onTranslationChange((ns, lng) => {
        if (ns === namespace && lng === language) {
          resolve();

          cancel();
        }
      });
    });
  }

  STATE.namespacesInUse.add(namespace);

  STATE.namespacesResolving[language + "~" + namespace] = true;

  if (parentLanguage) {
    await resolveTranslation(namespace, parentLanguage);
  }

  const translation = await runTranslationResolver(namespace, language);

  if (parentLanguage) {
    STATE.translations[language][namespace] = Object.assign(
      {},
      STATE.translations[parentLanguage][namespace],
      translation
    );
  } else {
    STATE.translations[language][namespace] = translation;
  }

  // console.log(
  //   `@opendash/i18n: resolved`,
  //   language,
  //   namespace,
  //   STATE.translations[language][namespace]
  // );

  STATE.namespacesResolving[language + "~" + namespace] = false;

  for (const callback of Array.from(STATE.translationChangeListener)) {
    Promise.resolve()
      .then(() => {
        if (typeof callback === "function") {
          callback(namespace, language);
        } else {
          console.error(
            `@opendash/i18n: callback is not a function:`,
            callback
          );
        }
      })
      .catch((error) => {
        console.error(`@opendash/i18n: Error in namespace change listener:`);
        console.error(error);
      });
  }
}

/**
 * Flattens a nested translation object into a flat object with dot-separated keys.
 *
 * @param pack - The nested translation object to flatten.
 * @param prefix - The prefix to use for the keys in the flat object.
 * @param result - The resulting flat translation object.
 * @param separator - The separator to use between nested keys.
 * @returns The flat translation object.
 */
function flattenTranslation(
  pack: Translation,
  prefix: string = "",
  result: TranslationFlat = {},
  separator: string = "."
): TranslationFlat {
  for (const key of Object.keys(pack)) {
    if (Array.isArray(pack[key])) {
      console.warn(
        "@opendash/i18n: Arrays are not allowed in Translations",
        pack[key]
      );
    } else if (typeof pack[key] === "object" && pack[key] !== null) {
      flattenTranslation(
        pack[key] as Translation,
        prefix ? prefix + separator + key : key,
        result
      );
    } else {
      result[prefix ? prefix + separator + key : key] = pack[key] as string;
    }
  }

  return result;
}

/**
 * Registers a callback function to be called when a translation changes.
 *
 * @param callback - The callback function to register.
 * @returns A function to unregister the callback.
 */
export function onTranslationChange(
  callback: TranslationChangeListenerCallback
) {
  const STATE: I18NState = I18NState.getInstance();
  STATE.translationChangeListener.add(callback);

  return () => {
    STATE.translationChangeListener.delete(callback);
  };
}
